import { PlanejamentoDoAssistente, TradutorAssistente } from '../../dominio/assistente.interface'

/**
 * Notificação do navegador quando um plano do Assistente termina.
 *
 * Três regras, todas de respeito à pessoa:
 *  - a permissão só é pedida por GESTO (o clique em "Executar plano" ou em
 *    "Avisar quando terminar"), nunca no carregamento — pedir sem contexto é
 *    o caminho mais curto para o "bloquear" definitivo;
 *  - com `denied` ou sem a API (navegador antigo, webview), nada acontece;
 *  - um plano notifica UMA vez por aba: a marca fica no `sessionStorage`, e
 *    a `tag` igual ao id do plano faz o sistema trocar, e não empilhar, um
 *    aviso que escape do dedupe (duas abas, por exemplo).
 *
 * Sem Web Push: a notificação só sai com a aba aberta em algum lugar.
 */

export type PermissaoDeNotificacao = 'default' | 'granted' | 'denied' | 'indisponivel'

/** O pedaço da API `Notification` que o módulo usa — o dublê dos testes imita isto. */
export interface ApiDeNotificacao {
  readonly permission: string
  requestPermission (): Promise<string>
  new (titulo: string, opcoes?: { body?: string, tag?: string }): unknown
}

export interface ArmazenamentoDaAba {
  getItem (chave: string): string | null
  setItem (chave: string, valor: string): void
}

export function chaveDoPlanejamentoNotificado (planejamentoId: string): string {
  return `assistentePontotel.planejamentoNotificado.${planejamentoId}`
}

function apiDoNavegador (): ApiDeNotificacao | null {
  try {
    const api = (globalThis as { Notification?: ApiDeNotificacao }).Notification
    return typeof api === 'function' ? api : null
  } catch {
    return null
  }
}

function armazenamentoDaAba (): ArmazenamentoDaAba | null {
  try {
    const armazenamento = (globalThis as { sessionStorage?: ArmazenamentoDaAba }).sessionStorage
    return armazenamento && typeof armazenamento.getItem === 'function' ? armazenamento : null
  } catch {
    return null
  }
}

function normalizar (permissao: unknown): PermissaoDeNotificacao {
  return permissao === 'granted' || permissao === 'denied' || permissao === 'default'
    ? permissao
    : 'indisponivel'
}

export function permissaoDeNotificacao (api: ApiDeNotificacao | null = apiDoNavegador()): PermissaoDeNotificacao {
  if (!api) return 'indisponivel'
  try {
    return normalizar(api.permission)
  } catch {
    return 'indisponivel'
  }
}

/**
 * Chamar SÓ de dentro de um clique. Pede apenas quando a pessoa ainda não
 * decidiu; quem já disse sim ou não não é perguntado de novo.
 */
export async function pedirPermissaoPorGesto (
  api: ApiDeNotificacao | null = apiDoNavegador()
): Promise<PermissaoDeNotificacao> {
  const atual = permissaoDeNotificacao(api)
  if (!api || atual !== 'default') return atual
  try {
    return normalizar(await api.requestPermission())
  } catch {
    return permissaoDeNotificacao(api)
  }
}

/** Título e corpo do aviso, com os números da visão do plano. */
export function textoDaNotificacao (
  planejamento: PlanejamentoDoAssistente,
  t: TradutorAssistente
): { titulo: string, corpo: string } {
  const { resumo } = planejamento
  const problemas = resumo.falha + resumo.interrompidaPeloSupervisor
  const parametros = {
    titulo: planejamento.titulo,
    executadas: resumo.executado,
    total: resumo.total,
    problemas
  }
  return {
    titulo: t('assistentePontotel.planejamento.notificacao.titulo'),
    corpo: problemas > 0
      ? t('assistentePontotel.planejamento.notificacao.corpoComFalhas', parametros)
      : t('assistentePontotel.planejamento.notificacao.corpo', parametros)
  }
}

/**
 * Avisa que o plano terminou. Só para `concluido` e `concluido_com_falhas` —
 * cancelado é decisão da própria pessoa, não novidade. Devolve se avisou.
 */
export function notificarFimDoPlanejamento (
  planejamento: PlanejamentoDoAssistente,
  t: TradutorAssistente,
  dependencias: { api?: ApiDeNotificacao | null, armazenamento?: ArmazenamentoDaAba | null } = {}
): boolean {
  if (planejamento.estado !== 'concluido' && planejamento.estado !== 'concluido_com_falhas') return false
  const api = dependencias.api === undefined ? apiDoNavegador() : dependencias.api
  if (!api || permissaoDeNotificacao(api) !== 'granted') return false

  const armazenamento = dependencias.armazenamento === undefined ? armazenamentoDaAba() : dependencias.armazenamento
  const chave = chaveDoPlanejamentoNotificado(planejamento.id)
  try {
    if (armazenamento?.getItem(chave)) return false
    armazenamento?.setItem(chave, '1')
  } catch {
    // Sem armazenamento da aba o aviso ainda sai: a `tag` evita a pilha.
  }

  const { titulo, corpo } = textoDaNotificacao(planejamento, t)
  try {
    // eslint-disable-next-line no-new
    new api(titulo, { body: corpo, tag: planejamento.id })
    return true
  } catch {
    // Alguns navegadores só aceitam notificação pelo service worker; sem ele,
    // o fim do plano continua visível no cartão.
    return false
  }
}

export function chaveDaRespostaConsolidadaNotificada (interacaoId: string): string {
  return `assistentePontotel.respostaConsolidadaNotificada.${interacaoId}`
}

/**
 * Avisa que a resposta consolidada de um plano de consulta chegou à conversa.
 * É o aviso do plano de consulta: o fim da execução não avisa nada, porque a
 * pessoa espera a RESPOSTA, e dois avisos para a mesma pergunta seriam ruído.
 *
 * As mesmas regras do aviso de fim de plano: só com permissão já concedida,
 * uma vez por aba (marca no `sessionStorage` pela interação, que é fixa por
 * plano) e `tag` igual à interação, para o sistema trocar em vez de empilhar.
 * Devolve se avisou.
 */
export function notificarRespostaConsolidada (
  planejamento: Pick<PlanejamentoDoAssistente, 'titulo'>,
  interacaoId: string,
  t: TradutorAssistente,
  dependencias: { api?: ApiDeNotificacao | null, armazenamento?: ArmazenamentoDaAba | null } = {}
): boolean {
  if (!interacaoId) return false
  const api = dependencias.api === undefined ? apiDoNavegador() : dependencias.api
  if (!api || permissaoDeNotificacao(api) !== 'granted') return false

  const armazenamento = dependencias.armazenamento === undefined ? armazenamentoDaAba() : dependencias.armazenamento
  const chave = chaveDaRespostaConsolidadaNotificada(interacaoId)
  try {
    if (armazenamento?.getItem(chave)) return false
    armazenamento?.setItem(chave, '1')
  } catch {
    // Sem armazenamento da aba o aviso ainda sai: a `tag` evita a pilha.
  }

  const titulo = t('assistentePontotel.planejamento.notificacao.tituloDaResposta')
  const corpo = planejamento.titulo
    ? t('assistentePontotel.planejamento.notificacao.corpoDaResposta', { titulo: planejamento.titulo })
    : t('assistentePontotel.planejamento.notificacao.corpoDaRespostaSemTitulo')
  try {
    // eslint-disable-next-line no-new
    new api(titulo, { body: corpo, tag: interacaoId })
    return true
  } catch {
    // Sem notificação, a resposta continua na conversa.
    return false
  }
}
